import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { House, Zap, Target, TrendingUp, User, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useAppState } from "@/context/StateContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { BackToTop } from "@/components/BackToTop";

const NAV = [
  { to: "/app", label: "Home", icon: House, id: "home", end: true },
  { to: "/goal", label: "My Goal", icon: Target, id: "my-goal" },
  { to: "/progress", label: "Progress", icon: TrendingUp, id: "progress" },
  { to: "/profile", label: "Profile", icon: User, id: "profile" },
];

const WORKFLOW_PREFIXES = ["/sprint", "/foundation"];
const isWorkflowPath = (p) => WORKFLOW_PREFIXES.some((w) => p.startsWith(w));

const BrandWordmark = ({ className = "" }) => (
  <span className={`font-serif leading-none text-[#171717] ${className}`} data-testid="brand-name">
    The Paycheck Edit<span className="align-top text-[0.6em] text-[#C9A86A]">™</span>
  </span>
);

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#164C3B] text-white">
      <Zap className="h-5 w-5" strokeWidth={2.4} />
    </span>
    <BrandWordmark className="text-xl" />
  </div>
);

// Compact mobile-only header (<768px). Primary destinations show the brand;
// workflow screens additionally show a back control. Desktop/tablet already
// carry the brand in the sidebar / top bar, so no header is duplicated there.
function MobileHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const workflow = isWorkflowPath(pathname);
  return (
    <header
      className="sticky top-0 z-40 flex h-14 items-center justify-center border-b border-[#E5E0D9] bg-[#FAF8F4]/90 px-4 backdrop-blur-md md:hidden"
      data-testid="mobile-app-header"
    >
      {workflow && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="mobile-back-button"
          aria-label="Back"
          className="absolute left-2 flex min-h-10 w-10 items-center justify-center rounded-lg text-[#164C3B] hover:bg-[#F0EBE3]"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      <BrandWordmark className="text-lg" />
    </header>
  );
}

const FOOTER_LINKS = ["Terms", "Privacy", "Educational Disclaimer", "Support"];

// Compact footer that scrolls with page content (never a fixed bar).
function AppFooter() {
  return (
    <footer
      className="mt-12 border-t border-[#E5E0D9] pt-8 text-center md:text-left"
      data-testid="app-footer"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="max-w-md md:max-w-sm">
          <BrandWordmark className="text-base" />
          <p className="mt-2 text-xs leading-relaxed text-[#70584A]">
            Educational financial planning tool. It never moves your money. You decide and confirm every proposed move shown.
          </p>
        </div>
        <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 md:justify-end" aria-label="Footer">
          {FOOTER_LINKS.map((label, i) => (
            <span key={label} className="flex items-center gap-3">
              <button
                type="button"
                data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toast(`${label} is coming soon.`)}
                className="text-xs font-medium text-[#525252] transition-colors hover:text-[#164C3B] hover:underline"
              >
                {label}
              </button>
              {i < FOOTER_LINKS.length - 1 && <span className="text-[#C9C2B8]">·</span>}
            </span>
          ))}
        </nav>
      </div>
      <p className="mt-6 text-xs text-[#A89A88]">
        © 2026 The Paycheck Edit<span className="align-top text-[0.7em] text-[#C9A86A]">™</span>
      </p>
    </footer>
  );
}

function Loader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="app-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E5E0D9] border-t-[#2F6B57]" />
    </div>
  );
}

export function AppLayout({ children }) {
  const { loading } = useAppState();

  return (
    <div className="min-h-screen bg-[#FAF8F4] text-[#171717]" data-testid="app-header">
      <ScrollToTop />
      <BackToTop />
      {/* Desktop persistent left sidebar (>=1200px) */}
      <aside
        className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-[#E5E0D9] bg-white px-5 py-7 desktop:flex"
        data-testid="desktop-sidebar-nav"
      >
        <div className="px-1">
          <Logo />
        </div>
        <nav className="mt-10 flex flex-1 flex-col gap-1.5">
          {NAV.map(({ to, label, icon: Icon, id, end }) => (
            <NavLink
              key={id}
              to={to}
              end={end}
              data-testid={`nav-item-${id}`}
              className={({ isActive }) =>
                `flex min-h-[44px] items-center gap-3 rounded-xl px-3.5 text-[15px] font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#DDE7DF] text-[#164C3B]"
                    : "text-[#525252] hover:bg-[#F5F2EC] hover:text-[#171717]"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>
        <p className="px-2 text-xs leading-relaxed text-[#70584A]">
          The Paycheck Edit shows the move. You make the decision.
        </p>
      </aside>

      {/* Tablet top bar (768px - 1199px) */}
      <header
        className="sticky top-0 z-40 hidden items-center justify-between border-b border-[#E5E0D9] bg-white/90 px-6 py-3 backdrop-blur-md md:flex desktop:hidden"
        data-testid="tablet-top-nav"
      >
        <Logo />
        <nav className="flex items-center gap-1">
          {NAV.map(({ to, label, icon: Icon, id, end }) => (
            <NavLink
              key={id}
              to={to}
              end={end}
              data-testid={`nav-item-tablet-${id}`}
              className={({ isActive }) =>
                `flex min-h-[44px] items-center gap-2 rounded-xl px-3 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#DDE7DF] text-[#164C3B]"
                    : "text-[#525252] hover:bg-[#F5F2EC]"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
              <span className="hidden lg:inline">{label}</span>
            </NavLink>
          ))}
        </nav>
      </header>

      {/* Compact mobile header (<768px) */}
      <MobileHeader />

      {/* Main content */}
      <main className="desktop:pl-64">
        <div className="mx-auto w-full max-w-[1180px] px-5 pb-28 pt-6 sm:px-6 md:pb-12 md:pt-8 desktop:px-12">
          {loading ? (
            <Loader />
          ) : (
            <>
              {children}
              <AppFooter />
            </>
          )}
        </div>
      </main>

      {/* Mobile bottom navigation (<768px) */}
      <nav
        className="fixed bottom-0 left-0 z-50 flex h-16 w-full items-stretch border-t border-[#E5E0D9] bg-white/95 backdrop-blur-md md:hidden"
        data-testid="mobile-bottom-nav"
      >
        {NAV.map(({ to, label, icon: Icon, id, end }) => (
          <NavLink
            key={id}
            to={to}
            end={end}
            data-testid={`nav-item-mobile-${id}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                isActive ? "text-[#2F6B57]" : "text-[#70584A]"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 2} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
